import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { WithdrawalsApiAdapter } from '../adapters';
import {
  CreateWithdrawalPayload,
  GeneratePaymentFilesPayload,
  MarkPaidBulkPayload,
  MarkPaidPayload,
  RejectWithdrawalPayload,
} from '../models';
import { WITHDRAWALS_QUERY_KEY } from './useWithdrawals';

export const useWithdrawalsMutations = () => {
  const queryClient = useQueryClient();
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(() => new WithdrawalsApiAdapter(httpClient), [httpClient]);

  const invalidateList = () => {
    queryClient.invalidateQueries({ queryKey: [WITHDRAWALS_QUERY_KEY] });
  };

  const createMutation = useMutation({
    mutationFn: ({ walletId, payload }: { walletId: number; payload: CreateWithdrawalPayload }) =>
      apiAdapter.createWithdrawal(walletId, payload),
    onSuccess: invalidateList,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => apiAdapter.markCancel(id),
    onSuccess: invalidateList,
  });

  const markPaidMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload?: MarkPaidPayload }) => apiAdapter.markPaid(id, payload),
    onSuccess: invalidateList,
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: RejectWithdrawalPayload }) =>
      apiAdapter.markReject(id, payload),
    onSuccess: invalidateList,
  });

  const markReturnedMutation = useMutation({
    mutationFn: (id: number) => apiAdapter.markReturned(id),
    onSuccess: invalidateList,
  });

  const generatePaymentFilesMutation = useMutation({
    mutationFn: (payload: GeneratePaymentFilesPayload) => apiAdapter.generatePaymentFiles(payload),
    onSuccess: invalidateList,
  });

  const markPaidBulkMutation = useMutation({
    mutationFn: (payload: MarkPaidBulkPayload) => apiAdapter.markPaidBulk(payload),
    onSuccess: invalidateList,
  });

  return {
    createWithdrawal: createMutation,
    cancelWithdrawal: cancelMutation,
    markPaid: markPaidMutation,
    rejectWithdrawal: rejectMutation,
    markReturned: markReturnedMutation,
    generatePaymentFiles: generatePaymentFilesMutation,
    markPaidBulk: markPaidBulkMutation,
  };
};
